/**
 * CommunityJournalPage - Single column centered layout
 * 
 * Features:
 * - 16:9 cover image
 * - Inline stats below cover (creator, items, views, copies)
 * - Key topics section
 * - Collapsible learning goals
 * - Content blocks list
 */

import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useCommunityJournal } from "@/hooks/useCommunityJournals";
import { useCopyJournalToWorkspace } from "@/hooks/useCopyJournalToWorkspace";
import { useJournalBlocks } from "@/hooks/useJournalBlocks";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ContentBlockList } from "@/components/community/ContentBlockList";
import { TopicTodoList } from "@/components/community/TopicTodoList";
import { TopicKeyTopics } from "@/components/community/TopicKeyTopics";
import {
    ArrowLeft,
    Play,
    Link2,
    Check,
    User,
    ChevronRight,
    BookOpen,
    Eye,
    Copy
} from "lucide-react";

const CommunityJournalPage = () => {
    const { id: journalId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { data: journal, isLoading: journalLoading, error } = useCommunityJournal(journalId);
    const { data: blocks, isLoading: blocksLoading } = useJournalBlocks(journalId);
    const copyMutation = useCopyJournalToWorkspace();

    // Sticky CTA state
    const [showStickyBar, setShowStickyBar] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setShowStickyBar(window.scrollY > 300);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Increment view count on mount
    useEffect(() => {
        if (journalId) {
            supabase.functions
                .invoke('journal-sharing-metrics', {
                    body: { journalId, action: 'view' },
                })
                .catch((err) => console.warn('Failed to increment view count:', err));
        }
    }, [journalId]);

    const handleCopy = () => {
        if (!user) {
            navigate(`/login?returnTo=/community/journal/${journalId}`);
            return;
        }
        if (journalId) {
            copyMutation.mutate(journalId);
        }
    };

    if (journalLoading) {
        return (
            <AppLayout pageTitle="Loading...">
                <div className="max-w-5xl mx-auto px-6 py-8">
                    <Skeleton className="h-4 w-32 mb-6" />
                    <Skeleton className="aspect-video w-full rounded-2xl mb-6" />
                    <Skeleton className="h-10 w-3/4 mb-3" />
                    <Skeleton className="h-5 w-1/2 mb-6" />
                    <div className="space-y-3">
                        <Skeleton className="h-16 w-full rounded-xl" />
                        <Skeleton className="h-16 w-full rounded-xl" />
                        <Skeleton className="h-16 w-full rounded-xl" />
                    </div>
                </div>
            </AppLayout>
        );
    }

    if (error || !journal) {
        return (
            <AppLayout pageTitle="Topic Not Found">
                <div className="max-w-3xl mx-auto px-6 py-16 text-center">
                    <span className="text-6xl mb-4 block">📚</span>
                    <h1 className="text-2xl font-semibold text-slate-900 mb-2">
                        Topic Not Found
                    </h1>
                    <p className="text-slate-500 mb-8">
                        This topic doesn't exist or is no longer available.
                    </p>
                    <Link to="/community">
                        <Button variant="outline" className="gap-2">
                            <ArrowLeft className="w-4 h-4" />
                            Back to Community
                        </Button>
                    </Link>
                </div>
            </AppLayout>
        );
    }

    const keyTopics = (journal as any).ai_context?.key_topics || [];

    return (
        <AppLayout pageTitle={journal.name}>
            {/* Sticky Bottom Bar (mobile + on scroll) */}
            <div className={`fixed bottom-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-t border-slate-200/60 px-4 py-3 transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'
                } lg:hidden`}>
                <Button
                    onClick={handleCopy}
                    disabled={copyMutation.isPending}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full shadow-lg shadow-slate-900/10"
                >
                    <Play className="w-4 h-4 mr-2 fill-current" />
                    {copyMutation.isPending ? 'Adding...' : 'Start Learning'}
                </Button>
            </div>

            {/* Main Content */}
            <div className="min-h-screen bg-[#FCFAF8]">
                <div className="max-w-5xl mx-auto px-6 py-8 md:py-12">

                    {/* Breadcrumb - Minimal */}
                    <nav className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-8 font-medium">
                        <Link to="/community" className="hover:text-slate-600 transition-colors">
                            Community
                        </Link>
                        <ChevronRight className="w-3 h-3 text-slate-300" />
                        {journal.tags?.[0] && (
                            <>
                                <Link
                                    to={`/community?tag=${encodeURIComponent(journal.tags[0])}`}
                                    className="hover:text-slate-600 transition-colors"
                                >
                                    {journal.tags[0]}
                                </Link>
                                <ChevronRight className="w-3 h-3 text-slate-300" />
                            </>
                        )}
                        <span className="text-slate-600 truncate max-w-[200px]">
                            Overview
                        </span>
                    </nav>

                    {/* Header Section: Centered & Airy */}
                    <div className="max-w-3xl mx-auto text-center mb-10">
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight tracking-tight">
                            {journal.name}
                        </h1>

                        {/* Metadata / Stats - Unified Line */}
                        <div className="flex items-center justify-center flex-wrap gap-4 text-sm text-slate-500 mb-8">
                            {/* Creator */}
                            <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 shadow-sm max-w-[200px]" title={`Created by ${(journal as any).profiles?.full_name || 'Anonymous'}`}>
                                {(journal as any).profiles?.avatar_url ? (
                                    <img
                                        src={(journal as any).profiles.avatar_url}
                                        alt=""
                                        className="w-5 h-5 rounded-full flex-shrink-0"
                                    />
                                ) : (
                                    <User className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                )}
                                <div className="flex items-baseline gap-1 overflow-hidden">
                                    <span className="text-xs text-slate-400 font-medium flex-shrink-0">by</span>
                                    <span className="font-medium text-slate-700 truncate">{(journal as any).profiles?.full_name || 'Anonymous'}</span>
                                </div>
                            </div>

                            {/* Stats */}
                            <div className="flex items-center gap-6 px-2">
                                <div className="flex items-center gap-2 text-slate-500" title="Items">
                                    <BookOpen className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{(blocks?.length || 0)} Items</span>
                                </div>
                                <div className="flex items-center gap-2 text-slate-500" title="Views">
                                    <Eye className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{(journal.view_count || 0)} Views</span>
                                </div>
                                <div className="flex items-center gap-2 text-slate-500" title="Copies">
                                    <Copy className="w-4 h-4 text-slate-400" />
                                    <span className="font-medium">{(journal.copy_count || 0)} Copies</span>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-center gap-4">
                            <Button
                                onClick={handleCopy}
                                disabled={copyMutation.isPending}
                                size="lg"
                                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 h-12 shadow-xl shadow-slate-900/10 transition-all hover:scale-105 active:scale-95"
                            >
                                <Play className="w-4 h-4 mr-2 fill-current" />
                                {copyMutation.isPending ? 'Adding...' : 'Start Learning'}
                            </Button>
                            <Button
                                variant="outline"
                                size="lg"
                                className="rounded-full px-6 h-12 border-slate-200 bg-white hover:bg-slate-50 hover:text-slate-900 text-slate-600 transition-all"
                                onClick={() => {
                                    navigator.clipboard.writeText(window.location.href);
                                    setLinkCopied(true);
                                    setTimeout(() => setLinkCopied(false), 2000);
                                }}
                            >
                                {linkCopied ? (
                                    <>
                                        <Check className="w-4 h-4 mr-2 text-green-600" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Link2 className="w-4 h-4 mr-2" />
                                        Share
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Cover Image - Floating Card Style */}
                    <div className="relative mb-12 max-w-4xl mx-auto">
                        {journal.community_cover_url ? (
                            <div className="relative aspect-[21/9] md:aspect-[2.5/1] rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/50 border border-slate-100">
                                <img
                                    src={journal.community_cover_url}
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ) : (
                            <div className="aspect-[21/9] md:aspect-[2.5/1] rounded-2xl md:rounded-3xl bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center border border-slate-100 shadow-sm">
                                <span className="text-6xl opacity-20">📚</span>
                            </div>
                        )}
                    </div>


                    {/* Content Section */}
                    <div className="max-w-3xl mx-auto space-y-10">
                        {/* Description */}
                        {journal.community_description && (
                            <div className="prose prose-slate prose-lg max-w-none text-slate-600 leading-relaxed text-center">
                                <p>{journal.community_description}</p>
                            </div>
                        )}

                        {/* Topics & Divider */}
                        <div>
                            {/* Tags */}
                            {journal.tags && journal.tags.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-2 mb-8">
                                    {journal.tags.map((tag: string, idx: number) => (
                                        <Link
                                            key={idx}
                                            to={`/community?tag=${encodeURIComponent(tag)}`}
                                            className="px-4 py-1.5 bg-white border border-slate-100 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-sm rounded-full transition-all shadow-sm"
                                        >
                                            {tag}
                                        </Link>
                                    ))}
                                </div>
                            )}

                            <TopicKeyTopics topics={keyTopics} />
                        </div>

                        {/* Learning Goals */}
                        <div className="mb-8">
                            <TopicTodoList todos={(journal as any).todo_items} />
                        </div>

                        {/* Content Blocks */}
                        <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-100 -z-10 md:left-8" />
                            <ContentBlockList
                                blocks={blocks}
                                isLoading={blocksLoading}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </AppLayout>
    );
};

export default CommunityJournalPage;
